import { clsx } from "clsx";
import pluralize from "pluralize";
import { ReactNode, useState } from "react";
import { Disclosure, DisclosurePanel } from "react-aria-components";
import { HStack, Icons, ItemTile, SymbolIcon, TriggerButton, TriggerButtonSize, VStack } from "widgets";

import { useGetOrganismsQuery } from "@/modules/workflow/graphql/coordinate-lists.gql.ts";

import style from "./VariantsTile.module.css";

interface VariantsTileProps {
  selectedGenomeId: number | null;
  restrictionVariantGenomesIds: number[];
  selectedVariantsCount?: number;
  isLoadingView?: boolean;
  message?: string | null;
}

export const VariantsTileSkeleton = ({ count }: { count?: number }) => (
  <>
    <ItemTile
      name="loading..."
      icon={<img src={Icons.Tiles.Entity} width={20} height={20} alt={undefined} />}
      id={"loading"}
      isDisabled
    />
    {!!count && (
      <HStack className={style.count}>
        <SymbolIcon icon="subdirectory_arrow_right" />
        {count} {pluralize("variant", count)}
      </HStack>
    )}
  </>
);

export const VariantsTile = ({
  selectedGenomeId,
  restrictionVariantGenomesIds,
  selectedVariantsCount,
  isLoadingView,
  message,
}: VariantsTileProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { data, loading } = useGetOrganismsQuery({
    variables: { orgIds: [selectedGenomeId!, ...restrictionVariantGenomesIds] },
    skip: selectedGenomeId == null,
  });

  if (selectedGenomeId == null) {
    return (
      <ItemTile
        name={message ?? "No Variants"}
        icon={<img src={Icons.Tiles.Entity} width={20} height={20} alt={undefined} />}
        id={"no"}
        isDisabled
      />
    );
  }

  if (loading || isLoadingView) {
    return <VariantsTileSkeleton count={selectedVariantsCount} />;
  }

  const [genome, ...variants] = data?.organisms ?? [];

  let genomeTile: ReactNode;

  if (message) {
    genomeTile = (
      <ItemTile
        name={message}
        icon={<img src={Icons.Tiles.Entity} width={20} height={20} alt={undefined} />}
        id={message}
      />
    );
  } else if (genome?.hidden) {
    genomeTile = (
      <ItemTile name="Restricted Genome" icon={<SymbolIcon icon="security" size={20} />} id="genome" isDisabled />
    );
  } else {
    genomeTile = (
      <ItemTile
        name={genome?.name ?? "Unknown Genome"}
        icon={<img src={Icons.Tiles.Entity} width={20} height={20} alt={undefined} />}
        id="genome"
      />
    );
  }

  return (
    <Disclosure isExpanded={isExpanded} onExpandedChange={setIsExpanded}>
      <HStack>
        {genomeTile}
        <TriggerButton
          slot="trigger"
          icon={isExpanded ? "unfold_less" : "unfold_more"}
          label={
            variants.length
              ? `${variants.length} variant ${pluralize("genome", variants.length)}`
              : "No variant genomes"
          }
          badge={String(variants.length)}
          badgeIntent="secondary"
          size={TriggerButtonSize.Small}
        />
      </HStack>
      <DisclosurePanel>
        <VStack className={style.panel} spacing={3}>
          <span className={style.subtitle}>
            {variants.length ? `Variant ${pluralize("genome", variants.length)}` : "No variant genomes"}
          </span>
          {variants.map((genome) =>
            genome?.hidden ? (
              <ItemTile
                key={genome?.id}
                name="Restricted Genome"
                icon={<SymbolIcon icon="security" size={20} />}
                id={genome?.id}
                isDisabled
              />
            ) : (
              <ItemTile
                key={genome?.id}
                name={genome?.name ?? "Unknown Genome"}
                icon={<img src={Icons.Tiles.Entity} width={20} height={20} alt={undefined} />}
                id={genome?.id}
              />
            ),
          )}
        </VStack>
      </DisclosurePanel>
      {!!selectedVariantsCount && (
        <HStack className={clsx(style.count, { [style.count_open]: isExpanded })}>
          <SymbolIcon icon="subdirectory_arrow_right" />
          {selectedVariantsCount} {pluralize("variant", selectedVariantsCount)}
        </HStack>
      )}
    </Disclosure>
  );
};
